import { NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AvatarComponent } from '@joii/design-system';
import { getAuth } from 'firebase/auth';
import { LucideAngularModule } from 'lucide-angular';
import { ApplyPipe } from 'ngxtension/call-apply';
import { filter, map } from 'rxjs';
import { PageWrapperComponent } from 'src/shared/components/design/pagewrapper/pagewrapper.component';
import { SwitchRegionComponent } from 'src/shared/components/switchregion/switchregion.component';
import { CheckAccessDataService } from 'src/shared/services/checkaccess.data.service/checkaccess.data.service';
import { PortalHeader, PortalHeaderItem, PortalHeaderItemType } from './menu.type';
import { hasAccessToItem, isActiveRoute, isPathHidden } from './menu.utils';

// Sidebar menu for the portal site
@Component({
  selector: 'app-menu',
  host: { '[class.sidebar-hidden]': 'sidebarHiddenClass' },
  imports: [
    PageWrapperComponent,
    NgTemplateOutlet,
    LucideAngularModule,
    ApplyPipe,
    SwitchRegionComponent,
    AvatarComponent,
  ],
  template: `
    @if (!isSidebarHidden()) {
      <nav>
        <!-- Site logo -->
        <img
          src="https://firebasestorage.googleapis.com/v0/b/vetai-e3e47.appspot.com/o/joii%2FJoii_logo.svg?alt=media&token=9046e258-a5ca-49a3-a22c-5fb78e920d15"
          alt="JOII PORTAL"
          class="px-2 md:px-4"
        />
        <!-- Region Switcher -->
        <div class="region-switcher shrink-0 w-full flex justify-center p-1 md:p-4">
          <app-switchregion class="md:w-full block"></app-switchregion>
        </div>
        <!-- Menu Items -->
        <div class="menu-items" [class.has-active-item]="hasActiveItem()">
          @for (item of visibleMenuItems(); track item.id) {
            <a
              class="menu-item-link text-gray-700 text-sm font-normal font-['Inter'] leading-5 flex gap-2 items-center"
              [attr.active]="isActiveRoute | apply: item : currentPath()"
              [attr.data-automationid]="item.dataautomationid"
              tabindex="0"
              (click)="menuClicked(item)"
              (keydown.enter)="menuClicked(item)"
              (keydown.space)="menuClicked(item)"
              [attr.title]="item.title"
            >
              <lucide-angular [img]="item.icon" [size]="16" />
              <span class="menu-item-label">{{ item.title }}</span>
            </a>
          }
        </div>
      </nav>
    }
    @if (pagewrapper()) {
      <!-- Page Wrapper -->
      <app-pagewrapper [setWhitebackground]="whitebackground()">
        @if (submenu()) {
          <div class="flex flex-row-reverse">
            <joii-avatar
              tabindex="0"
              (click)="accountclicked()"
              (keydown.enter)="accountclicked()"
              (keydown.space)="accountclicked()"
              class="cursor-pointer"
              variant="light-skin-brown-hair-female"
              size="md"
            />
          </div>
        }
        <ng-container *ngTemplateOutlet="contentTpl"></ng-container>
      </app-pagewrapper>
    } @else {
      <!-- Full Width Content -->
      <div [class.bg-white]="whitebackground()">
        <ng-container *ngTemplateOutlet="contentTpl"></ng-container>
      </div>
    }
    <ng-template #contentTpl><ng-content select="[slot=content]"></ng-content></ng-template>
  `,
  styles: `
    :host {
      display: flex;
      height: 100%;
      --menu-width: 223px;
      --menu-item-transition-duration: 0ms;
      --menu-item-transition: linear(
        0,
        0.029 1.6%,
        0.123 3.5%,
        0.651 10.6%,
        0.862 14.1%,
        1.002 17.7%,
        1.046 19.6%,
        1.074 21.6%,
        1.087 23.9%,
        1.086 26.6%,
        1.014 38.5%,
        0.994 46.3%,
        1
      );
    }
    :host.sidebar-hidden {
      --menu-width: 0;
    }
    :host > nav {
      flex-shrink: 0;
    }
    :host > *:not(nav) {
      flex: 1;
      min-width: 0;
      margin-left: var(--menu-width);
    }
    :host.sidebar-hidden > * {
      margin-left: 0;
    }
    nav {
      position: fixed;
      top: 0;
      left: 0;
      width: var(--menu-width);
      height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 24px;
      padding: 24px 0 20px 0;
      border-right: 1px solid #e5e7eb;
      background: #f8f9fa;
      transition: width 0.2s ease-in-out;
    }
    .menu-items {
      display: flex;
      flex-direction: column;
      width: 199px;
      padding: 0 12px;
      gap: 4px;
      min-height: 0;
      flex: 1;
      overflow-y: auto;
    }
    .menu-items:not(.has-active-item)::after {
      opacity: 0;
      pointer-events: none;
    }
    .menu-items::after {
      content: '';
      background: #2c5aa0;
      border-radius: 10px;
      corner-shape: squircle;
      position: absolute;
      position-anchor: --active-nav;
      left: anchor(left);
      right: anchor(right);
      top: anchor(top);
      bottom: anchor(bottom);
      z-index: -1;
      transition: inset var(--menu-item-transition-duration);
      transition-timing-function: var(--menu-item-transition);
    }
    .menu-items a.menu-item-link {
      cursor: pointer;
      height: 40px;
      min-height: 40px;
      flex-shrink: 0;
      border-radius: 10px;
      padding-left: 12px;
      transition: all var(--menu-item-transition-duration);
      transition-timing-function: var(--menu-item-transition);
    }
    .menu-items a.menu-item-link[active='true'] {
      anchor-name: --active-nav;
      color: #fff;
    }
    .menu-footer {
      flex-shrink: 0;
    }

    @media (max-width: 768px) {
      :host {
        --menu-width: 56px;
      }
      nav {
        padding: 12px 0 16px;
        gap: 16px;
      }
      nav .logo {
        font-size: 1rem;
        line-height: 1.25rem;
      }
      .menu-items {
        width: 100%;
        padding: 0 8px;
      }
      .menu-items a.menu-item-link {
        justify-content: center;
        padding-left: 0;
        gap: 0;
      }
      .menu-items a.menu-item-link .menu-item-label {
        display: none;
      }
    }
  `,
})
export class MenuComponent implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly checkAccessDataService = inject(CheckAccessDataService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly transitionDuration = 1000;

  ngAfterViewInit(): void {
    this.host.nativeElement.style.setProperty('--menu-item-transition-duration', `${this.transitionDuration}ms`);
  }

  menu = input<PortalHeader>();
  whitebackground = input(false);
  submenu = input(true);
  routerEvents = this.router.events;
  routerNavigated = toSignal(this.routerEvents.pipe(filter((event) => event instanceof NavigationEnd)));
  currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly currentPath = computed(() => {
    const raw = this.currentUrl();
    return raw?.split('?')[0]?.split('#')[0] ?? '';
  });

  readonly isActiveRoute = isActiveRoute;

  readonly isSidebarHidden = computed(() =>
    isPathHidden(this.currentPath(), this.currentUrl() ?? '', this.menu()?.hiddenpaths)
  );

  readonly visibleMenuItems = computed(() => {
    this.routerNavigated();
    const items = this.menu()?.items ?? [];
    const userRoles = this.checkAccessDataService.getRolesData();
    return items.filter((item) => hasAccessToItem(item, userRoles));
  });

  readonly hasActiveItem = computed(() => {
    const path = this.currentPath();
    return this.visibleMenuItems().some((item) => isActiveRoute(item, path));
  });

  get sidebarHiddenClass(): boolean {
    return this.isSidebarHidden();
  }

  pagewrapper = computed(() => {
    const menu = this.menu();
    const hidden = menu?.hiddenpagewrappers;
    this.routerNavigated();
    if (!hidden || hidden.length === 0) return true;
    return !hidden.some((wrapper) => this.router.url.includes(wrapper));
  });

  menuClicked(item: PortalHeaderItem) {
    switch (item.type) {
      case PortalHeaderItemType.Path:
        this.router.navigate([item.id]);
        break;
      case PortalHeaderItemType.TokenCopier:
        async function getToken() {
          const currentUser = getAuth()?.currentUser;
          let token = '';
          if (currentUser) {
            const idTokenResult = await currentUser.getIdTokenResult(false);
            if (idTokenResult) {
              token = idTokenResult.token;
            }
          }
          try {
            await navigator.clipboard.writeText(
              JSON.stringify({
                Authorization: token,
                'Accept-Language': 'en',
              })
            );
          } catch (err) {
            console.error('Failed to copy: ', err);
          }
        }

        getToken();
    }
  }

  accountclicked() {
    this.router.navigate([this.menu()?.accountpath]);
  }
}
